import { lazy, Suspense, useEffect, useState } from 'react';
import { useUser, SignIn, RedirectToSignIn, startMicrosoftSignIn } from './lib/auth';
import WorkspaceSignIn from './components/WorkspaceSignIn';
import { loginHintsFor, getWorkspaceChoice } from './lib/workspaceChoice';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RBACProvider } from './contexts/RBACContext';
import { BrandingProvider } from './contexts/BrandingContext';
// [split W2-A1] Platform-operator tenant switcher ("enter tenant" / "return to
// platform"). Wraps the authenticated shell only, so the signed-out sign-in /
// branding path below stays byte-identical. When no tenant is entered it sends
// no header and shows no UI — a normal FNS user sees zero change.
import { TenantSwitchProvider } from './contexts/TenantSwitchContext';
// [split W1-B] Loading/failure fallback for the sign-in header: the FOUNDING
// (FNS) identity, env-overridable. The backend resolves the real per-host
// identity (tenant / platform / founding) into /public/branding; this is only
// shown until that arrives, keeping FNS's sign-in byte-identical.
import { PUBLIC_BRANDING_DEFAULTS } from './lib/publicBranding';
import api, { PUBLIC_API_BASE } from './lib/api';
import AppShell from './components/Layout/AppShell';
import PageSpinner from './components/PageSpinner';
import RootErrorBoundary from './components/RootErrorBoundary';
import RoleLanding from './components/RoleLanding';

// ─── Eager (hot-path pages) ─────────────────────────────────────────────────
import Dashboard from './pages/Dashboard';
import CandidateList from './pages/candidates/CandidateList';
import JobList from './pages/jobs/JobList';
import SubmissionList from './pages/submissions/SubmissionList';
import Pipeline from './pages/Pipeline';
import CandidateTracker from './pages/CandidateTracker';
import KanbanBoard from './pages/KanbanBoard';
import Tasks from './pages/Tasks';

// ─── Lazy (loaded on demand) ────────────────────────────────────────────────
// Recruiting
const CandidateNew = lazy(() => import('./pages/candidates/CandidateNew'));
const CandidateDetail = lazy(() => import('./pages/candidates/CandidateDetail'));
const HrInterviewQueue = lazy(() => import('./pages/candidates/HrInterviewQueue'));
const PendingUpdateTracker = lazy(() => import('./pages/recruiting/PendingUpdateTracker'));
const ClientApprovals = lazy(() => import('./pages/ClientApprovals'));
const InboundDocs = lazy(() => import('./pages/InboundDocs'));
// [phase4 track-A] Unified conversations inbox (two-way SMS + email)
const Conversations = lazy(() => import('./pages/Conversations'));
const SecurityAgent = lazy(() => import('./pages/security/SecurityAgent'));
const MyDay = lazy(() => import('./pages/MyDay'));
const TeamLifecycle = lazy(() => import('./pages/TeamLifecycle'));
const OfferLetters = lazy(() => import('./pages/hr/OfferLetters'));
// [track-D payroll] Phase 3 Money Engine — payroll export page.
const Payroll = lazy(() => import('./pages/Payroll'));
// [track-C invoices] Client invoicing + accounts receivable (Phase 3).
const Invoices = lazy(() => import('./pages/Invoices'));
const WebsiteSecurityCheck = lazy(() => import('./pages/security/WebsiteSecurityCheck'));
const SecurityReadiness = lazy(() => import('./pages/security/SecurityReadiness'));
const Interviews = lazy(() => import('./pages/Interviews'));
const JobCandidateChecklist = lazy(() => import('./pages/jobs/JobCandidateChecklist'));
const JobNew = lazy(() => import('./pages/jobs/JobNew'));
const JobDetail = lazy(() => import('./pages/jobs/JobDetail'));
const MatchingCandidates = lazy(() => import('./pages/recruiting/MatchingCandidates'));
const JobEdit = lazy(() => import('./pages/jobs/JobEdit'));
const SubmissionDetail = lazy(() => import('./pages/submissions/SubmissionDetail'));
const ClientOrgList = lazy(() => import('./pages/clients/ClientOrgList'));
const ClientOrgDetail = lazy(() => import('./pages/clients/ClientOrgDetail'));
const AtsReports = lazy(() => import('./pages/AtsReports'));
const WeeklyRecruitingReport = lazy(() => import('./pages/WeeklyRecruitingReport'));
const Reminders = lazy(() => import('./pages/Reminders'));
const RoleDashboard = lazy(() => import('./pages/RoleDashboard'));

// Intelligence / AI
const Integrations = lazy(() => import('./pages/Integrations'));
const VendorIntegrations = lazy(() => import('./pages/admin/VendorIntegrations'));
const OnboardingSettings = lazy(() => import('./pages/admin/OnboardingSettings'));
const DemoData = lazy(() => import('./pages/admin/DemoData'));
// [phase6 track-F] Migration concierge — new-tenant import wizard (/import).
const TenantImport = lazy(() => import('./pages/admin/TenantImport'));
const Tenants = lazy(() => import('./pages/admin/Tenants'));
// [phase6 track-D] Tenant-admin self-serve cockpit (branding/seats/billing/flags).
// Gated in the Sidebar behind the tenant.admin.view perm; the page also
// self-guards so a direct URL hit shows a no-access panel rather than data.
const TenantAdmin = lazy(() => import('./pages/TenantAdmin'));
const TimesheetFlags = lazy(() => import('./pages/TimesheetFlags'));
const Briefing = lazy(() => import('./pages/Briefing'));
const Reports = lazy(() => import('./pages/Reports'));
const CompanyKnowledge = lazy(() => import('./pages/CompanyKnowledge'));
const ClarificationCenter = lazy(() => import('./pages/ClarificationCenter'));
const Templates = lazy(() => import('./pages/Templates'));
const AITeam = lazy(() => import('./pages/AITeam'));
const AITeamTaskDetail = lazy(() => import('./pages/AITeamTaskDetail'));
const AIMissions = lazy(() => import('./pages/AIMissions'));
const AIMissionDetail = lazy(() => import('./pages/AIMissionDetail'));
const MyAssignedWork = lazy(() => import('./pages/MyAssignedWork'));
const Suggestions = lazy(() => import('./pages/Suggestions'));
const DailySummary = lazy(() => import('./pages/DailySummary'));
const AIAssistant = lazy(() => import('./pages/AIAssistant'));
const AIKnowledgeBase = lazy(() => import('./pages/AIKnowledgeBase'));
const AIBrain = lazy(() => import('./pages/AIBrain'));
const TrainingHub = lazy(() => import('./pages/TrainingHub'));
const SetupWizard = lazy(() => import('./pages/SetupWizard'));
const AILearning = lazy(() => import('./pages/AILearning'));
const DocumentQA = lazy(() => import('./pages/DocumentQA'));

// Time Tracking
const TimeTracking = lazy(() => import('./pages/TimeTracking'));
const Attendance = lazy(() => import('./pages/Attendance'));
const TimeTrackingManager = lazy(() => import('./pages/TimeTrackingManager'));
const TimeTrackingAdmin = lazy(() => import('./pages/TimeTrackingAdmin'));

// eSign — heavy (pdf + signature)
const SignDocument = lazy(() => import('./pages/SignDocument'));
const ReferenceForm = lazy(() => import('./pages/ReferenceForm'));
const RenewalForm = lazy(() => import('./pages/RenewalForm'));
const EmployeeRenewals = lazy(() => import('./pages/EmployeeRenewals'));
const AttendancePolicy = lazy(() => import('./pages/AttendancePolicy'));
const ESignDashboard = lazy(() => import('./pages/esign/ESignDashboard'));
const ESignDocuments = lazy(() => import('./pages/esign/ESignDocuments'));
const ESignDocumentNew = lazy(() => import('./pages/esign/ESignDocumentNew'));
const ESignDocumentDetail = lazy(() => import('./pages/esign/ESignDocumentDetail'));
const ESignAnalytics = lazy(() => import('./pages/esign/ESignAnalytics'));
const ESignPrepare = lazy(() => import('./pages/esign/ESignPrepare'));
const ESignTemplates = lazy(() => import('./pages/esign/ESignTemplates'));
// ESignTemplatePrepare is intentionally not imported anymore —
// /esign/templates/:id/prepare now reuses ESignPrepare. The file is
// kept on disk for one release in case the unified editor needs to
// be reverted; future cleanup PR will delete it.
const ESignForms = lazy(() => import('./pages/esign/ESignForms'));

// Compliance — 25+ pages, all lazy
const ComplianceReports = lazy(() => import('./pages/compliance/ComplianceReports'));
const JointCommission = lazy(() => import('./pages/compliance/JointCommission'));
const MyCertificates = lazy(() => import('./pages/compliance/MyCertificates'));
const CertificateVerify = lazy(() => import('./pages/compliance/CertificateVerify'));
// Phase 6.5 — public client portal (token-auth, no sign-in required)
const ClientPortalView = lazy(() => import('./pages/ClientPortalView'));
const ConfirmContact = lazy(() => import('./pages/ConfirmContact'));
// [track-B approvals] Phase 3 Money Engine — client timesheet sign-off
const TimesheetApprovals = lazy(() => import('./pages/TimesheetApprovals'));
const TimesheetApprovalPublic = lazy(() => import('./pages/TimesheetApprovalPublic'));
// [phase4 track-B] Public candidate interview self-scheduling (token-auth via URL)
const InterviewSchedule = lazy(() => import('./pages/InterviewSchedule'));
// Public candidate document upload (token-auth via URL)
const CandidateUpload = lazy(() => import('./pages/CandidateUpload'));
const CandidatePortal = lazy(() => import('./pages/CandidatePortal'));
const CandidateDocPortal = lazy(() => import('./pages/CandidateDocPortal'));
// [phase4 track-E] Nurse Hub — public branded hub token page.
const NurseHub = lazy(() => import('./pages/NurseHub'));
const ComplianceAdminHub = lazy(() => import('./pages/compliance/ComplianceAdminHub'));
const CategoryManager = lazy(() => import('./pages/compliance/CategoryManager'));
const PolicyList = lazy(() => import('./pages/compliance/PolicyList'));
const PolicyEditor = lazy(() => import('./pages/compliance/PolicyEditor'));
const DocumentList = lazy(() => import('./pages/compliance/DocumentList'));
const DocumentEditor = lazy(() => import('./pages/compliance/DocumentEditor'));
const MyCompliance = lazy(() => import('./pages/compliance/MyCompliance'));
const PolicySign = lazy(() => import('./pages/compliance/PolicySign'));
const DocumentView = lazy(() => import('./pages/compliance/DocumentView'));
const ComplianceRecords = lazy(() => import('./pages/compliance/ComplianceRecords'));
const ExamList = lazy(() => import('./pages/compliance/ExamList'));
const ExamEditor = lazy(() => import('./pages/compliance/ExamEditor'));
const ChecklistList = lazy(() => import('./pages/compliance/ChecklistList'));
const ChecklistEditor = lazy(() => import('./pages/compliance/ChecklistEditor'));
const BundleList = lazy(() => import('./pages/compliance/BundleList'));
const BundleEditor = lazy(() => import('./pages/compliance/BundleEditor'));
const BundleAssign = lazy(() => import('./pages/compliance/BundleAssign'));
const TakeExam = lazy(() => import('./pages/compliance/TakeExam'));
const CompleteChecklist = lazy(() => import('./pages/compliance/CompleteChecklist'));
const NotificationSettings = lazy(() => import('./pages/compliance/NotificationSettings'));
// Phase 2 additions
const DocTypesAdmin = lazy(() => import('./pages/compliance/DocTypesAdmin'));
const PolicyAIWizard = lazy(() => import('./pages/compliance/PolicyAIWizard'));
const ExamAIWizard = lazy(() => import('./pages/compliance/ExamAIWizard'));
const ChecklistAIWizard = lazy(() => import('./pages/compliance/ChecklistAIWizard'));
const CourseList = lazy(() => import('./pages/compliance/CourseList'));
const CourseEditor = lazy(() => import('./pages/compliance/CourseEditor'));
const CourseViewer = lazy(() => import('./pages/compliance/CourseViewer'));
const PlacementReadinessAdmin = lazy(() => import('./pages/compliance/PlacementReadinessAdmin'));
const BulkAssign = lazy(() => import('./pages/compliance/BulkAssign'));
const MessageCenter = lazy(() => import('./pages/compliance/MessageCenter'));

// Role dashboards
const CEODashboard = lazy(() => import('./pages/dashboards/CEODashboard'));
const CeoBriefing = lazy(() => import('./pages/CeoBriefing'));
const PerformanceScorecard = lazy(() => import('./pages/PerformanceScorecard'));
const ClientHealth = lazy(() => import('./pages/ClientHealth'));
const Performance = lazy(() => import('./pages/Performance'));
const ManagementDashboard = lazy(() => import('./pages/dashboards/ManagementDashboard'));
const RecruitingDashboard = lazy(() => import('./pages/dashboards/RecruitingDashboard'));
const CompanyCertifications = lazy(() => import('./pages/CompanyCertifications'));
const Renewals = lazy(() => import('./pages/Renewals'));
const MyCeus = lazy(() => import('./pages/MyCeus'));
const HRDashboard = lazy(() => import('./pages/dashboards/HRDashboard'));
const CredentialingDashboard = lazy(() => import('./pages/dashboards/CredentialingDashboard'));
const ComplianceDashboard = lazy(() => import('./pages/dashboards/ComplianceDashboard'));

// Workforce
const StaffManagement = lazy(() => import('./pages/StaffManagement'));
const StaffProfile = lazy(() => import('./pages/StaffProfile'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Credentialing = lazy(() => import('./pages/Credentialing'));
const ComplianceMonitor = lazy(() => import('./pages/ComplianceMonitor'));
const OnboardingPackets = lazy(() => import('./pages/OnboardingPackets'));
const NewHires = lazy(() => import('./pages/NewHires'));
const OnboardingBundle = lazy(() => import('./pages/OnboardingBundle'));
const Placements = lazy(() => import('./pages/Placements'));
const Checklists = lazy(() => import('./pages/Checklists'));

// Operations & Controls
const Clients = lazy(() => import('./pages/Clients'));
const DocumentChecker = lazy(() => import('./pages/DocumentChecker'));
const EmailMonitor = lazy(() => import('./pages/EmailMonitor'));
const SMSApprovals = lazy(() => import('./pages/SMSApprovals'));
const Incidents = lazy(() => import('./pages/Incidents'));
const Timekeeping = lazy(() => import('./pages/Timekeeping'));
// Phase 4.4
const Scheduling = lazy(() => import('./pages/Scheduling'));
const PTO = lazy(() => import('./pages/PTO'));
const DocumentLogs = lazy(() => import('./pages/DocumentLogs'));
// [track-E openshifts] internal board + public token claim page
const OpenShifts = lazy(() => import('./pages/OpenShifts'));
const ClaimShift = lazy(() => import('./pages/ClaimShift'));

// Business
const BusinessDev = lazy(() => import('./pages/BusinessDev'));
const ActionPlan = lazy(() => import('./pages/ActionPlan'));
const Huddle = lazy(() => import('./pages/Huddle'));
const EndOfDay = lazy(() => import('./pages/EndOfDay'));
const SmartSearch = lazy(() => import('./pages/SmartSearch'));
const BulkLookup = lazy(() => import('./pages/BulkLookup'));
const MatchAlerts = lazy(() => import('./pages/MatchAlerts'));
const Sequences = lazy(() => import('./pages/Sequences'));
const CheckinRate = lazy(() => import('./pages/CheckinRate'));
const Careers = lazy(() => import('./pages/Careers'));
const CareerJobDetail = lazy(() => import('./pages/CareerJobDetail'));
const SchedulerAdmin = lazy(() => import('./pages/admin/Scheduler'));
const Insurance = lazy(() => import('./pages/Insurance'));
const Funding = lazy(() => import('./pages/Funding'));
const Timeline = lazy(() => import('./pages/Timeline'));
const Contracts = lazy(() => import('./pages/Contracts'));

// Admin / Account
const SettingsIndex = lazy(() => import('./pages/settings/SettingsIndex'));
const ExtensionTokens = lazy(() => import('./pages/settings/ExtensionTokens'));
const TrackingDomains = lazy(() => import('./pages/settings/TrackingDomains'));
const ProjectsManagement = lazy(() => import('./pages/settings/ProjectsManagement'));
const RecruitingPlaybooks = lazy(() => import('./pages/recruiting/RecruitingPlaybooks'));
const RecruitingPlaybookDetail = lazy(() => import('./pages/recruiting/RecruitingPlaybookDetail'));
const RecruitingPlaybookEditor = lazy(() => import('./pages/recruiting/RecruitingPlaybookEditor'));
const CallPlaybooks = lazy(() => import('./pages/playbooks/CallPlaybooks'));
const CallPlaybookRunPage = lazy(() => import('./pages/playbooks/CallPlaybookRun'));
const AiAdvisors = lazy(() => import('./pages/ai-advisors/AiAdvisors'));
const AiAdvisorChat = lazy(() => import('./pages/ai-advisors/AiAdvisorChat'));
const AiAdvisorsPlaceholder = lazy(() => import('./pages/ai-advisors/AiAdvisorsPlaceholder'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const NotificationPrefs = lazy(() => import('./pages/admin/NotificationPrefs'));
const Security = lazy(() => import('./pages/Security'));
const IntegrationSettings = lazy(() => import('./pages/IntegrationSettings'));
const AutomationsAdmin = lazy(() => import('./pages/admin/AutomationsAdmin'));
const AutomationApprovals = lazy(() => import('./pages/admin/AutomationApprovals'));
const BoardRoom = lazy(() => import('./pages/BoardRoom'));
const GovGrants = lazy(() => import('./pages/GovGrants'));
const ErrorLog = lazy(() => import('./pages/admin/ErrorLog'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));

// Phase 8 — RBAC admin pages
const RolesPermissions = lazy(() => import('./pages/admin/RolesPermissions'));
const UserAccessPage    = lazy(() => import('./pages/admin/UserAccess'));
const SecurityAuditLog  = lazy(() => import('./pages/admin/SecurityAuditLog'));
// [phase6 track-H] Access Transparency — tenant-facing cross-tenant access log.
const AccessTransparency = lazy(() => import('./pages/admin/AccessTransparency'));
const UserAuditDetail   = lazy(() => import('./pages/admin/UserAuditDetail'));
const MyPermissions     = lazy(() => import('./pages/admin/MyPermissions'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const ApiDocs = lazy(() => import('./pages/admin/ApiDocs'));
const ImportTracker = lazy(() => import('./pages/admin/ImportTracker'));
const Webhooks = lazy(() => import('./pages/admin/Webhooks'));
const AdminFiles = lazy(() => import('./pages/admin/AdminFiles'));
const RateCards = lazy(() => import('./pages/admin/RateCards'));
const JobBoards = lazy(() => import('./pages/admin/JobBoards'));
const DnuLog = lazy(() => import('./pages/compliance/DnuLog'));
const PlacementHolds = lazy(() => import('./pages/compliance/PlacementHolds'));
const DeficiencyLog = lazy(() => import('./pages/compliance/DeficiencyLog'));
const NamingConventions = lazy(() => import('./pages/compliance/NamingConventions'));
const Benefits = lazy(() => import('./pages/Benefits'));
const ReferralPayouts = lazy(() => import('./pages/ReferralPayouts'));
const NotFound = lazy(() => import('./pages/NotFound'));
const StaffShell = lazy(() => import('./components/Layout/StaffShell'));

import ViewAsRoleBanner from './components/admin/ViewAsRoleBanner';

// DEV BYPASS — set localStorage.setItem('fnsai_dev_bypass','1') in console to skip auth
const DEV_BYPASS = import.meta.env.DEV && localStorage.getItem('fnsai_dev_bypass') === '1';

// AILearning still only has a redirect route; silence the unused-import flag.
// DocumentQA is now rendered at /document-qa again.
void AILearning;

// Pre-login branding by DOMAIN (white-label Tranche 5): a visitor on a
// tenant-registered custom domain sees that client's name/logo on the
// sign-in screen. [split W1-B] The backend now returns a populated `branding`
// for EVERY host — a tenant custom domain, the platform's own host
// (is_platform → the platform name), or today's unmatched founding-default
// host (→ "FNS AI"). So render whatever it returns regardless of `matched`;
// until it resolves (or if it can't be reached) the caller falls back to the
// founding default, keeping FNS byte-identical. Public endpoint — no auth yet.
// `matched` comes back alongside the branding and answers a second question the
// sign-in screen needs: does this HOST already say which company the visitor
// belongs to? On a tenant's own domain it does, and asking them to type it would
// be silly. On the platform's shared host it does not, so we ask.
interface LoginHost {
  branding: { app_name?: string; tagline?: string; logo_url?: string | null } | null;
  matched: boolean | null;   // null while unresolved
  // Is this the platform's OWN shared host (is_platform), where the URL says
  // nothing about which tenant a visitor belongs to? Only there do we ask
  // "which company?". On a tenant's own domain the host already says who they
  // are; on today's single-tenant founding-default host there is only one
  // company, so asking has no valid answer and dead-ends the sign-in.
  isPlatform: boolean | null;   // null while unresolved
}

function useLoginBranding(enabled: boolean): LoginHost {
  const [state, setState] = useState<LoginHost>({ branding: null, matched: null, isPlatform: null });
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api.get(`/public/branding?host=${encodeURIComponent(window.location.hostname)}`)
      .then(r => {
        if (cancelled) return;
        setState({
          branding: r.data?.branding?.app_name ? r.data.branding : null,
          matched: r.data?.matched === true,
          isPlatform: r.data?.is_platform === true,
        });
      })
      .catch(() => { /* founding default is always a safe render */ });
    return () => { cancelled = true; };
  }, [enabled]);
  return state;
}

// A host we don't recognise as a tenant's should not serve a deep app route to
// a stranger. It used to send them to '/', which on the platform's OWN host is
// the marketing page — and since `is_platform` is true for that host too, every
// direct load of an app route bounced straight back to marketing. Clicking
// "Sign in" returned you to where you started, forever, without ever reaching
// Microsoft. This was the reported "it just brings me back to the marketing site".
//
// Now it sends them to the sign-in screen, which asks which company they are
// signing in to. That is a useful destination on any host, known or not, and it
// cannot loop: /sign-in is exempt below.
const SIGN_IN_PATH = '/sign-in';

function useUnknownPlatformHostRedirect(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const path = window.location.pathname;
    // Already there — redirecting to the page you are on is the loop itself.
    if (path === SIGN_IN_PATH || path.startsWith(`${SIGN_IN_PATH}/`)) return;
    const isPublicLike = (
      path === '/' ||
      path === '/apply' ||
      path === '/careers' ||
      path.startsWith('/careers/') ||
      path === '/privacy' ||
      path === '/unsubscribe' ||
      /^\/(sign|upload|portal|docs|hub|verify-cert|rate|client-view|timesheet-approval|claim|interview-schedule)(\/|$)/.test(path)
    );
    if (isPublicLike) return;

    let cancelled = false;
    fetch(`${PUBLIC_API_BASE}/public/branding?host=${encodeURIComponent(window.location.hostname)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d?.matched === false && d?.is_platform === true) {
          window.location.replace(SIGN_IN_PATH);
        }
      })
      .catch(() => { /* fail open; keep the current route */ });
    return () => { cancelled = true; };
  }, [enabled]);
}

function App() {
  const { isLoaded, isSignedIn } = useUser();
  const loginBranding = useLoginBranding(isLoaded && !isSignedIn);
  // Signed-in users are never bounced: they have already answered the question
  // this redirect exists to ask, and moving them off a deep link they opened
  // deliberately is just losing their place.
  useUnknownPlatformHostRedirect(isLoaded && !isSignedIn);
  // If we've been "loading" too long, MSAL's inProgress is wedged in
  // 'startup'. Show a recovery card instead of an infinite spinner —
  // see the QA report on intermittent white-page sign-ins.
  const [authStuck, setAuthStuck] = useState(false);
  useEffect(() => {
    if (isLoaded) { setAuthStuck(false); return; }
    const t = setTimeout(() => setAuthStuck(true), 12_000);
    return () => clearTimeout(t);
  }, [isLoaded]);

  // DEV bypass — skip auth entirely for pressure testing
  if (DEV_BYPASS) return <TenantSwitchProvider><RBACProvider><BrandingProvider><AppRoutes /></BrandingProvider></RBACProvider></TenantSwitchProvider>;

  if (!isLoaded) {
    if (authStuck) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 24, fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
          <div style={{ maxWidth: 460, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '28px 32px', boxShadow: '0 12px 32px rgba(15,23,42,0.08)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 8 }}>Sign-in stuck</div>
            <h1 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 700, color: '#111827' }}>Still checking your sign-in…</h1>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: '#475569', lineHeight: 1.55 }}>
              This usually clears up with a reload. If it keeps happening, your browser may be blocking third-party cookies for Microsoft sign-in.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{ background: '#1565c0', color: '#fff', border: 0, borderRadius: 8, padding: '10px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--prd)',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid rgba(255,255,255,0.15)',
            borderTopColor: 'var(--ac)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isSignedIn) {
    // Public, token-authenticated pages must render WITHOUT a Microsoft
    // work-account login — the recipient is a candidate / signer / client,
    // not an org user, and the token in their URL is their credential.
    // Without this, opening a signing link while logged out dead-ended at
    // the Microsoft sign-in screen (candidates can't sign in). These render
    // outside RBAC too (no org permissions needed).
    // Public token pages: timesheet-approval (client sign-off) and claim
    // (open-shift claim) both render without a Microsoft sign-in — the URL
    // token is the recipient's credential.
    const publicPath = /^\/(sign|upload|portal|docs|hub|verify-cert|rate|careers|client-view|timesheet-approval|claim|interview-schedule|confirm-contact)(\/|$)/
      .test(window.location.pathname);
    if (publicPath) {
      return <AppRoutes />;
    }
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--prd)',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* Branding is host-driven: a registered custom domain shows that
            client's name/logo; the platform's own host shows the PLATFORM
            identity; today's unmatched founding-default host shows FNS. The
            backend resolves which; until it does (or if unreachable) we fall
            back to the founding default so FNS stays byte-identical. [split W1-B] */}
        {(() => {
          const b = loginBranding.branding ?? PUBLIC_BRANDING_DEFAULTS;
          return (
            <>
              {b.logo_url && (
                <img src={b.logo_url} alt="" style={{ height: 44, width: 'auto', marginBottom: 4, borderRadius: 6 }} />
              )}
              <div style={{ fontSize: 22, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
                {b.app_name}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>
                {b.tagline || 'Secure Login'}
              </div>
            </>
          );
        })()}
        {/* Ask "which company?" ONLY on the platform's OWN shared host, where the
            URL genuinely doesn't say which tenant the visitor belongs to. On a
            tenant's own domain the host already identifies the company, and on
            today's single-tenant founding-default host (the FNS deployment) there
            is exactly one company — asking there has no valid answer and dead-ends
            the sign-in (an employee typing their work email is told "we couldn't
            find that company" for the only company there is). Both of those go
            straight to the Microsoft button.
            A remembered company short-circuits the wait. Until the lookup resolves
            (isPlatform === null) the plain button is the safe render — it is what
            every tenant domain has always shown. */}
        {loginBranding.isPlatform === true || getWorkspaceChoice()
          ? <WorkspaceSignIn onSignIn={(choice) => startMicrosoftSignIn(loginHintsFor(choice))} />
          : <SignIn routing="hash" />}
      </div>
    );
  }

  return (
    <TenantSwitchProvider>
      <RBACProvider>
        <BrandingProvider>
          <AppRoutes />
        </BrandingProvider>
      </RBACProvider>
    </TenantSwitchProvider>
  );
}

function AppRoutes() {
  return (
    // Error boundary above Suspense catches crashes in lazy()-loaded page
    // modules too. Previous boundary was inside AppShell, so module-load
    // errors for a single lazy page (e.g. Reminders) crashed through the
    // whole shell. With the boundary here, a bad page shows an error card
    // but the user still has nothing, this at least prevents complete
    // blank page. An INNER boundary in AppShell keeps the shell alive for
    // per-page render errors.
    <RootErrorBoundary>
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        {/* Staff Console — same operations screens as Settings, in the new
            shell. The /settings/* routes below stay, so old links still work. */}
        <Route path="/platform" element={<StaffShell />}>
          <Route index element={<Navigate to="/platform/tenant-setup" replace />} />
          <Route path="tenant-setup" element={<Tenants />} />
          <Route path="scheduler" element={<SchedulerAdmin />} />
          <Route path="automations" element={<AutomationsAdmin />} />
          <Route path="error-log" element={<ErrorLog />} />
          <Route path="demo-data" element={<DemoData />} />
        </Route>
        <Route path="/" element={<AppShell />}>
          <Route index element={<RoleLanding />} />
          <Route path="dashboard" element={<Dashboard />} />

          {/* Business Dev */}
          <Route path="business-dev" element={<BusinessDev />} />

          {/* Business Ops */}
          <Route path="action-plan" element={<ActionPlan />} />
          <Route path="huddle" element={<Huddle />} />
          <Route path="huddle/:id" element={<Huddle />} />
          <Route path="end-of-day" element={<EndOfDay />} />
          <Route path="smart-search" element={<SmartSearch />} />
          <Route path="bulk-lookup" element={<BulkLookup />} />
          <Route path="match-alerts" element={<MatchAlerts />} />
          <Route path="interviews" element={<Interviews />} />
          <Route path="sequences" element={<Sequences />} />
          <Route path="settings/scheduler" element={<SchedulerAdmin />} />
          <Route path="insurance" element={<Insurance />} />
          <Route path="funding" element={<Funding />} />
          <Route path="timeline" element={<Timeline />} />
          <Route path="contracts" element={<Contracts />} />

          {/* Workforce */}
          <Route path="staff" element={<StaffManagement />} />
          <Route path="staff/:id" element={<StaffProfile />} />
          <Route path="onboarding" element={<Onboarding />} />
          <Route path="new-hires" element={<NewHires />} />
          <Route path="onboarding-packets" element={<OnboardingPackets />} />
          <Route path="onboarding-bundle" element={<OnboardingBundle />} />
          <Route path="employee-renewals" element={<EmployeeRenewals />} />
          <Route path="attendance-policy" element={<AttendancePolicy />} />
          <Route path="credentialing" element={<Credentialing />} />
          <Route path="credentialing/compliance-monitor" element={<ComplianceMonitor />} />
          <Route path="placements" element={<Placements />} />
          <Route path="checklists" element={<Checklists />} />
          {/* Phase 4.4 — scheduling + PTO */}
          <Route path="scheduling" element={<Scheduling />} />
          <Route path="pto" element={<PTO />} />
          {/* [track-E openshifts] internal open-shift broadcast board */}
          <Route path="open-shifts" element={<OpenShifts />} />

          {/* Operations */}
          <Route path="clients" element={<Clients />} />
          <Route path="email-monitor" element={<EmailMonitor />} />
          <Route path="sms" element={<SMSApprovals />} />
          <Route path="documents" element={<DocumentChecker />} />

          {/* Internal Controls */}
          <Route path="incidents" element={<Incidents />} />
          <Route path="timekeeping" element={<Timekeeping />} />
          {/* [track-B approvals] Client timesheet sign-off queue (internal) */}
          <Route path="timesheet-approvals" element={<TimesheetApprovals />} />
          <Route path="logs" element={<DocumentLogs />} />

          {/* Intelligence */}
          <Route path="integrations" element={<Integrations />} />
          <Route path="reports" element={<Reports />} />
          <Route path="knowledge" element={<CompanyKnowledge />} />
          <Route path="clarification" element={<ClarificationCenter />} />
          <Route path="templates" element={<Templates />} />
          <Route path="ai-team" element={<AITeam />} />
          <Route path="ai-team/:id" element={<AITeamTaskDetail />} />
          <Route path="ai-missions" element={<AIMissions />} />
          <Route path="ai-missions/:id" element={<AIMissionDetail />} />
          <Route path="my-work" element={<MyAssignedWork />} />
          <Route path="my-ceus" element={<MyCeus />} />
          <Route path="suggestions" element={<Suggestions />} />
          <Route path="daily-summary" element={<DailySummary />} />

          {/* AI System */}
          <Route path="ai-assistant" element={<AIAssistant />} />
          <Route path="ai-knowledge" element={<AIKnowledgeBase />} />
          {/* Redirect old AI URLs → consolidated AI Knowledge Base */}
          <Route path="knowledge"    element={<Navigate to="/ai-knowledge" replace />} />
          <Route path="ai-learning"  element={<Navigate to="/ai-knowledge" replace />} />
          <Route path="ai-brain" element={<AIBrain />} />
          <Route path="document-qa"  element={<DocumentQA />} />
          <Route path="training"     element={<TrainingHub />} />
          <Route path="setup-wizard" element={<SetupWizard />} />

          {/* Recruiting */}
          <Route path="candidates" element={<CandidateList />} />
          <Route path="candidates/hr-interviews" element={<HrInterviewQueue />} />
          <Route path="candidates/new" element={<CandidateNew />} />
          <Route path="candidates/:id" element={<CandidateDetail />} />
          <Route path="pipeline/tracker" element={<CandidateTracker />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="pending-tracker" element={<PendingUpdateTracker />} />
          <Route path="client-approvals" element={<ClientApprovals />} />
          <Route path="inbound-docs" element={<InboundDocs />} />
          {/* [phase4 track-A] Unified conversations inbox (two-way SMS + email) */}
          <Route path="conversations" element={<Conversations />} />
          <Route path="security/website-check" element={<WebsiteSecurityCheck />} />
          <Route path="security/readiness" element={<SecurityReadiness />} />
          <Route path="security/agent" element={<SecurityAgent />} />
          <Route path="my-day" element={<MyDay />} />
          <Route path="team-lifecycle" element={<TeamLifecycle />} />
          <Route path="hr/offer-letters" element={<OfferLetters />} />
          {/* [track-D payroll] Phase 3 Money Engine — payroll export (finance.payroll.view gated in nav) */}
          <Route path="finance/payroll" element={<Payroll />} />
          {/* [track-C invoices] Client invoicing + accounts receivable */}
          <Route path="finance/invoices" element={<Invoices />} />
          <Route path="reminders" element={<Reminders />} />
          <Route path="role-dashboard" element={<RoleDashboard />} />

          {/* ATS Phase 2+ */}
          <Route path="jobs" element={<JobList />} />
          <Route path="jobs/new" element={<JobNew />} />
          <Route path="jobs/:id/edit" element={<JobEdit />} />
          <Route path="jobs/:id" element={<JobDetail />} />
          <Route path="jobs/:id/match-candidates" element={<MatchingCandidates />} />
          <Route path="jobs/:id/candidate-checklist" element={<JobCandidateChecklist />} />
          <Route path="submissions" element={<SubmissionList />} />
          <Route path="submissions/:id" element={<SubmissionDetail />} />
          <Route path="clients-orgs" element={<ClientOrgList />} />
          <Route path="clients-orgs/:id" element={<ClientOrgDetail />} />
          <Route path="kanban" element={<KanbanBoard />} />
          <Route path="renewals" element={<Renewals />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="ats-reports" element={<AtsReports />} />
          <Route path="weekly-report" element={<WeeklyRecruitingReport />} />

          {/* Recruiting playbooks — structured call scripts */}
          <Route path="recruiting/playbooks" element={<RecruitingPlaybooks />} />
          <Route path="recruiting/playbooks/new" element={<RecruitingPlaybookEditor />} />
          <Route path="recruiting/playbooks/:idOrSlug/edit" element={<RecruitingPlaybookEditor />} />
          <Route path="recruiting/playbooks/:idOrSlug" element={<RecruitingPlaybookDetail />} />

          {/* Call Playbooks — interactive checkable forms (HCSS audit, exit survey, AI-generated) */}
          <Route path="call-playbooks" element={<CallPlaybooks />} />
          <Route path="call-playbooks/runs/:runId" element={<CallPlaybookRunPage />} />

          {/* AI Team — chat with the 19-agent internal team (route name kept
              as /ai-advisors for backward-compat URLs / saved chat threads). */}
          <Route path="ai-advisors" element={<AiAdvisors />} />
          <Route path="ai-advisors/chat/:threadId" element={<AiAdvisorChat />} />
          {/* AI Advisors — placeholder for future external-advisor personas */}
          <Route path="ai-advisors-coming-soon" element={<AiAdvisorsPlaceholder />} />

          {/* Compliance */}
          <Route path="/compliance/my" element={<MyCompliance />} />
          <Route path="/compliance/policy/:id" element={<PolicySign />} />
          <Route path="/compliance/document/:id" element={<DocumentView />} />
          <Route path="/compliance/admin" element={<ComplianceAdminHub />} />
          <Route path="/compliance/admin/categories" element={<CategoryManager />} />
          <Route path="/compliance/admin/doc-types" element={<DocTypesAdmin />} />
          <Route path="/compliance/admin/policies" element={<PolicyList />} />
          <Route path="/compliance/admin/policies/new" element={<PolicyEditor />} />
          <Route path="/compliance/admin/policies/:id/edit" element={<PolicyEditor />} />
          <Route path="/compliance/admin/policies/ai-wizard" element={<PolicyAIWizard />} />
          <Route path="/compliance/admin/documents" element={<DocumentList />} />
          <Route path="/compliance/admin/documents/new" element={<DocumentEditor />} />
          <Route path="/compliance/admin/documents/:id/edit" element={<DocumentEditor />} />
          <Route path="/compliance/admin/records" element={<ComplianceRecords />} />
          <Route path="/compliance/exam/:id" element={<TakeExam />} />
          <Route path="/compliance/checklist/:id" element={<CompleteChecklist />} />
          <Route path="/compliance/admin/exams" element={<ExamList />} />
          <Route path="/compliance/admin/exams/new" element={<ExamEditor />} />
          <Route path="/compliance/admin/exams/:id/edit" element={<ExamEditor />} />
          <Route path="/compliance/admin/exams/:id/ai-wizard" element={<ExamAIWizard />} />
          <Route path="/compliance/admin/checklists" element={<ChecklistList />} />
          <Route path="/compliance/admin/checklists/new" element={<ChecklistEditor />} />
          <Route path="/compliance/admin/checklists/:id/edit" element={<ChecklistEditor />} />
          <Route path="/compliance/admin/checklists/:id/ai-wizard" element={<ChecklistAIWizard />} />
          <Route path="/compliance/admin/bundles" element={<BundleList />} />
          <Route path="/compliance/admin/bundles/new" element={<BundleEditor />} />
          <Route path="/compliance/admin/bundles/:id/edit" element={<BundleEditor />} />
          <Route path="/compliance/admin/bundles/:id/assign" element={<BundleAssign />} />
          {/* Phase 2.6 — Courses. `/new` must come BEFORE `/:id/edit` so it
              doesn't get matched as id="new" by a stricter guard. */}
          <Route path="/compliance/admin/courses" element={<CourseList />} />
          <Route path="/compliance/admin/courses/new" element={<CourseEditor />} />
          <Route path="/compliance/admin/courses/:id/edit" element={<CourseEditor />} />
          <Route path="/compliance/courses/:id" element={<CourseViewer />} />
          <Route path="/compliance/course/:id" element={<CourseViewer />} />
          <Route path="/compliance/admin/notifications" element={<NotificationSettings />} />
          <Route path="/compliance/admin/readiness" element={<PlacementReadinessAdmin />} />
          <Route path="/compliance/admin/bulk-assign" element={<BulkAssign />} />
          <Route path="/compliance/messages" element={<MessageCenter />} />
          <Route path="/compliance/certificates" element={<MyCertificates />} />
          <Route path="/compliance/admin/reports" element={<ComplianceReports />} />
          <Route path="company-certifications" element={<CompanyCertifications />} />
          <Route path="/compliance/joint-commission" element={<JointCommission />} />
          <Route path="/compliance/dnu-log" element={<DnuLog />} />
          <Route path="/compliance/placement-holds" element={<PlacementHolds />} />
          <Route path="/compliance/deficiencies" element={<DeficiencyLog />} />
          <Route path="/compliance/naming-conventions" element={<NamingConventions />} />
          <Route path="benefits" element={<Benefits />} />
          <Route path="referrals" element={<ReferralPayouts />} />

          {/* eSign — full module */}
          <Route path="esign" element={<ESignDashboard />} />
          <Route path="esign/documents" element={<ESignDocuments />} />
          <Route path="esign/documents/new" element={<ESignDocumentNew />} />
          <Route path="esign/documents/:id" element={<ESignDocumentDetail />} />
          <Route path="esign/documents/:id/prepare" element={<ESignPrepare />} />
          <Route path="esign/templates" element={<ESignTemplates />} />
          {/* Template field placement now uses the unified ESignPrepare —
              same rich editor (sticky coord toolbar, click-to-capture,
              place-by-coords, font size, validation, arrow-key nudge,
              live preview, resize edges) as the document-side flow. */}
          <Route path="esign/templates/:id/prepare" element={<ESignPrepare />} />
          <Route path="esign/forms" element={<ESignForms />} />
          <Route path="esign/analytics" element={<ESignAnalytics />} />

          {/* Time Tracking */}
          <Route path="time-tracking" element={<TimeTracking />} />
          <Route path="attendance" element={<Attendance />} />
          <Route path="time-tracking/team" element={<TimeTrackingManager />} />
          <Route path="time-tracking/admin" element={<TimeTrackingAdmin />} />

          {/* Role Dashboards */}
          <Route path="ceo-dashboard" element={<CEODashboard />} />
          <Route path="daily-briefing" element={<CeoBriefing />} />
          <Route path="board-room" element={<BoardRoom />} />
          <Route path="gov-grants" element={<GovGrants />} />
          <Route path="team-scorecard" element={<PerformanceScorecard />} />
          <Route path="client-health" element={<ClientHealth />} />
          <Route path="performance" element={<PerformanceScorecard />} />
          <Route path="management-dashboard" element={<ManagementDashboard />} />
          <Route path="recruiting-dashboard" element={<RecruitingDashboard />} />
          <Route path="hr-dashboard" element={<HRDashboard />} />
          <Route path="credentialing-dashboard" element={<CredentialingDashboard />} />
          <Route path="compliance-dashboard" element={<ComplianceDashboard />} />

          {/* Account */}
          {/* Settings hub. The bare /settings used to 404 because
              every entry was a sub-route. /settings/security is an
              alias to /security so a sidebar copy or a stale bookmark
              doesn't dead-end the user. Both QA-reported. */}
          <Route path="settings" element={<SettingsIndex />} />
          <Route path="settings/security" element={<Navigate to="/security" replace />} />
          <Route path="security" element={<Security />} />
          <Route path="settings/users" element={<UserManagement />} />
          <Route path="settings/notifications" element={<NotificationPrefs />} />
          <Route path="settings/integrations" element={<IntegrationSettings />} />
          <Route path="settings/automations" element={<AutomationsAdmin />} />
          <Route path="automations/approvals" element={<AutomationApprovals />} />
          <Route path="settings/vendor-integrations" element={<VendorIntegrations />} />
          <Route path="settings/onboarding" element={<OnboardingSettings />} />
          <Route path="settings/demo-data" element={<DemoData />} />
          {/* [phase6 track-F] Migration concierge wizard — gated in the sidebar
              on tenant.import; the backend enforces the permission on write. */}
          <Route path="import" element={<TenantImport />} />
          <Route path="settings/tenants" element={<Tenants />} />
          {/* [phase6 track-D] Tenant-admin self-serve cockpit (permission-gated) */}
          <Route path="tenant-admin" element={<TenantAdmin />} />
          <Route path="timesheet-flags" element={<TimesheetFlags />} />
          <Route path="briefing" element={<Briefing />} />
          <Route path="settings/error-log" element={<ErrorLog />} />

          {/* Help Center — accessible to everyone regardless of role */}
          <Route path="help" element={<HelpCenter />} />
          <Route path="help/:articleId" element={<HelpCenter />} />

          {/* Phase 8 — RBAC admin */}
          <Route path="settings/roles" element={<RolesPermissions />} />
          <Route path="settings/user-access" element={<UserAccessPage />} />
          <Route path="settings/audit-log" element={<SecurityAuditLog />} />
          {/* [phase6 track-H] Access Transparency — a tenant sees every time
              platform staff touched their data (perm-gated in nav + backend). */}
          <Route path="settings/access-transparency" element={<AccessTransparency />} />
          <Route path="settings/audit-log/user/:userId" element={<UserAuditDetail />} />
          <Route path="settings/my-permissions" element={<MyPermissions />} />
          <Route path="notifications" element={<NotificationCenter />} />
          <Route path="settings/api-docs" element={<ApiDocs />} />
          <Route path="settings/import-tracker" element={<ImportTracker />} />
          <Route path="settings/webhooks" element={<Webhooks />} />
          <Route path="settings/file-library" element={<AdminFiles />} />
          <Route path="finance/rate-cards" element={<RateCards />} />
          <Route path="settings/job-boards" element={<JobBoards />} />
          <Route path="settings/extension-tokens" element={<ExtensionTokens />} />
          <Route path="settings/tracking-domains" element={<TrackingDomains />} />
          <Route path="settings/time-tracking-projects" element={<ProjectsManagement />} />

          {/* 404 — shows a real "page not found" card instead of silently
              redirecting to /dashboard. The previous Navigate-to-dashboard
              behavior masked typos and produced the QA-reported white-screen
              symptom (malformed URL → /dashboard → auth gate → /sign-in
              while MSAL ran in the background). */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* [track-E openshifts] Public claim page — token IS the credential,
            renders outside the app shell (no login). */}
        <Route path="/claim/:token" element={<ClaimShift />} />

        {/* Public signing page — no auth wrapper */}
        <Route path="/sign/:token" element={<SignDocument />} />

        {/* Public referee reference form — token-auth only */}
        <Route path="/reference/:token" element={<ReferenceForm />} />

        {/* Public employee renewal/time-off form — token-auth only */}
        <Route path="/renewal/:token" element={<RenewalForm />} />

        {/* Public certificate verification — no auth */}
        <Route path="/verify-cert/:number" element={<CertificateVerify />} />

        {/* Public new-hire check-in rating page (B1.b). Token-auth only. */}
        <Route path="/rate/:token/:initial" element={<CheckinRate />} />

        {/* Public careers — branded FNS job board, no auth */}
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/:id" element={<CareerJobDetail />} />

        {/* Phase 6.5 — Public client portal view. Token in URL is the
            only credential. Backend validates + revokes + expires. */}
        <Route path="/client-view/:token" element={<ClientPortalView />} />
        <Route path="/confirm-contact/:token" element={<ConfirmContact />} />

        {/* [track-B approvals] Public client timesheet sign-off. Token in URL
            is the only credential — tenant is derived from the token row. */}
        <Route path="/timesheet-approval/:token" element={<TimesheetApprovalPublic />} />

        {/* [phase4 track-B] Public candidate interview self-scheduling. Token in
            URL is the only credential — tenant is derived from the token row. */}
        <Route path="/interview-schedule/:token" element={<InterviewSchedule />} />

        {/* Public candidate document upload — recruiter generates a
            tokenised link and the candidate uploads without logging in.
            Same trust model as the eSign /sign/:token flow. */}
        <Route path="/upload/:token" element={<CandidateUpload />} />
        <Route path="/portal/:token" element={<CandidatePortal />} />
        {/* [phase4 track-E] Nurse Hub — one branded, mobile-first self-service
            surface issued the moment someone applies. Extends the hashed portal
            token; the three legacy routes above keep working for old links. */}
        <Route path="/hub/:token" element={<NurseHub />} />

        {/* Public candidate document portal — verify email (phone optional),
            upload docs that route straight to OneDrive, and e-sign queued
            forms. Token is the only credential. */}
        <Route path="/docs/:token" element={<CandidateDocPortal />} />

        <Route path="/sign-in/*" element={<RedirectToSignIn />} />
      </Routes>
    </Suspense>
    </RootErrorBoundary>
  );
}

export default App;
